import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const CODE = 'ESSENTIA-TRANSFERENCIA-5';
const MARK = 'Forma de pago: ';
const controls = () => Array.from(document.querySelectorAll('essentia-cart-payment'));
let cartState = null;
let busy = false;
let reconcileQueued = false;
let config = null;
const formStates = new WeakMap();
const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
const normalize = value => String(value || '').toUpperCase();
const matchesCode = value => normalize(value).includes(CODE);
const method = () => cartState?.attributes?.essentia_payment || controls()[0]?.dataset.selected || '';
const isPack = cart => (cart?.items || []).some(item => /pack-arabe/i.test(item.handle || '') || item.properties?._essentia_pack);
const directPackPayment = () => Boolean(document.querySelector('[data-essentia-pack-payment-direct]'));
const wantsDiscount = () => method() === 'transfer' && (cartState ? hasIndividual(cartState) : controls()[0]?.dataset.eligible === 'true');
const money = value => 'Gs. ' + new Intl.NumberFormat('es-PY', { maximumFractionDigits: 0 }).format(Number(value || 0) / 100);
const copy = key => config?.copy?.[key] || key;
const isEligible = item => {
  const title = String(item.variant_title || '').toLowerCase().replace(/[\s\u00a0]/g, '').replace(/[–-]/g, '—');
  const allowed = new Set(['3ml', '5ml', '10ml', '30ml', '3ml—paraprobar', '5ml—ideal', '10ml—mejorprecio', '30ml—paradisfrutar', '10ml—máselegido', '30ml—mejorvalor']);
  return String(item.product_type || '').trim().toLowerCase() === 'decant'
    && allowed.has(title)
    && !/combo|pack|frasco|perfume entero/i.test(item.product_title || '')
    && !/pack-arabe/i.test(item.handle || '')
    && !item.properties?._essentia_pack && !item.properties?._armatucombo;
};
const hasIndividual = cart => (cart?.items || []).some(isEligible);
const transferAllocation = item => (item.line_level_discount_allocations || []).reduce((sum, allocation) => sum + (matchesCode(allocation.discount_application?.title) ? Number(allocation.amount || 0) : 0), 0);
const validTransfer = cart => (cart?.items || []).every(item => {
  const amount = transferAllocation(item);
  return isEligible(item) ? amount === Math.round(Number(item.original_line_price || 0) * 0.05 / 100) * 100 : amount === 0;
});
const label = (value, cart = null) => {
  if (value !== 'transfer') return copy('cod');
  const pack = cart ? isPack(cart) : directPackPayment();
  const eligible = cart ? hasIndividual(cart) : controls()[0]?.dataset.eligible === 'true';
  return eligible ? copy('selected_transfer') : copy(pack ? 'selected_transfer_pack' : 'selected_transfer_excluded');
};

async function request(path, options = {}) {
  const response = await fetch(root() + path, { credentials: 'same-origin', cache: 'no-store', ...options });
  const data = await response.json();
  if (!response.ok || data.status >= 400) throw new Error(copy('error'));
  return data;
}

function noteFor(cart, selected) {
  const original = String(cart.note || '').split('\n').filter(line => !line.startsWith(MARK)).join('\n').trim();
  const paymentLine = MARK + label(selected, cart);
  return [original, paymentLine].filter(Boolean).join('\n');
}

function status(message = '', error = false) {
  for (const control of controls()) {
    control.dataset.error = String(error);
    const node = control.querySelector('[data-payment-status]');
    if (node && node.textContent !== message) node.textContent = message;
  }
}

function refreshButtons() {
  document.querySelectorAll('#cart-drawer button#rsi_buy_now_button,.cart-summary__inner button#rsi_buy_now_button').forEach(button => {
    const node = button.querySelector('.essentia-rsi-label');
    if (directPackPayment()) {
      if (node?.dataset.paymentOriginalLabel) {
        node.textContent = node.dataset.paymentOriginalLabel;
        node.dataset.essentiaCaption = node.dataset.paymentOriginalCaption;
        delete node.dataset.paymentOriginalLabel;
        delete node.dataset.paymentOriginalCaption;
      }
      button.removeAttribute('aria-busy');
      return;
    }
    if (!config) return;
    if (node && !node.dataset.paymentOriginalLabel) {
      node.dataset.paymentOriginalLabel = node.textContent;
      node.dataset.paymentOriginalCaption = node.dataset.essentiaCaption || '';
    }
    if (node && node.textContent !== copy('next')) node.textContent = copy('next');
    if (node && node.dataset.essentiaCaption !== copy('next_detail')) node.dataset.essentiaCaption = copy('next_detail');
    button.setAttribute('aria-busy', String(busy));
  });
}

function paint() {
  for (const control of controls()) {
    const selected = method();
    control.dataset.selected = selected;
    control.setAttribute('aria-busy', String(busy));
    for (const input of control.querySelectorAll('input[type="radio"]')) {
      input.checked = input.value === selected;
      input.disabled = busy;
    }
  }
  refreshButtons();
}

function notify(result) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise }));
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
    detail: { sections: result.sections || {}, items: result.items, itemCount: result.item_count, source: 'essentia-payment-choice', didError: false }
  });
}

async function choose(selected) {
  if (busy || directPackPayment() || !['cod', 'transfer'].includes(selected)) return;
  busy = true;
  paint();
  status(copy('saving'));
  let before;
  try {
    before = await request('cart.js');
    if (isPack(before)) return;
    const eligible = hasIndividual(before);
    const codes = (before.discount_codes || []).map(code => code.code).filter(code => !matchesCode(code));
    if (selected === 'transfer' && eligible) codes.push(CODE);
    const sections = Array.from(new Set(['cart-drawer-section', ...Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).map(el => el.dataset.sectionId).filter(Boolean)])).slice(0, 5);
    const result = await request('cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: { essentia_payment: selected, 'Forma de pago': label(selected, before) },
        note: noteFor(before, selected),
        discount: codes.join(','),
        sections,
        sections_url: window.location.pathname
      })
    });
    const applied = (result.discount_codes || []).some(code => matchesCode(code.code) && code.applicable);
    if (selected === 'transfer' && eligible && (!applied || !validTransfer(result))) {
      cartState = await request('cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { essentia_payment: before.attributes?.essentia_payment || '', 'Forma de pago': before.attributes?.['Forma de pago'] || '' }, note: before.note || '', discount: codes.filter(code => !matchesCode(code)).join(',') })
      });
      throw new Error(copy('discount_error'));
    }
    cartState = result;
    busy = false;
    notify(result);
    paint();
    status(copy('saved'));
    for (const dialog of document.querySelectorAll('#rsi_form_wrapper[open]')) syncForm(dialog);
  } catch (error) {
    if (before && !cartState) cartState = before;
    status(error.message || copy('error'), true);
  } finally {
    busy = false;
    paint();
    if (reconcileQueued) {
      reconcileQueued = false;
      queueMicrotask(() => choose(method()));
    }
  }
}

class EssentiaCartPayment extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    try {
      config = JSON.parse(this.querySelector('[data-payment-copy]').textContent);
    } catch { return; }
    this.addEventListener('change', event => {
      if (event.target.matches('input[type="radio"]')) choose(event.target.value);
    });
    paint();
    if (!cartState && !busy) request('cart.js').then(cart => { cartState = cart; schedule(); }).catch(() => status(copy('sync_error'), true));
    const expected = this.dataset.selected === 'transfer' && this.dataset.eligible === 'true';
    if (this.dataset.selected && expected !== (this.dataset.transferApplied === 'true')) {
      queueMicrotask(() => { if (this.isConnected && !busy) choose(this.dataset.selected); });
    }
  }
}
if (!customElements.get('essentia-cart-payment')) customElements.define('essentia-cart-payment', EssentiaCartPayment);

function setNative(field, value) {
  if (field.value === value) return;
  const proto = field instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value').set.call(field, value);
  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
}

function formError(dialog, message = '') {
  let node = dialog.querySelector('.essentia-form-payment__error');
  if (!node) {
    node = document.createElement('p');
    node.className = 'essentia-form-payment__error';
    node.setAttribute('role', 'alert');
    dialog.querySelector('.essentia-form-payment')?.after(node);
  }
  node.hidden = !message;
  if (node.textContent !== message) node.textContent = message;
}

const pygAmount = node => {
  const text = node?.textContent?.trim() || '';
  if (!text) return null;
  if (/gratis|free/i.test(text)) return 0;
  const digits = text.replace(/[^0-9]/g, '');
  return digits ? Number(digits) * 100 : null;
};
function formAmountsMatch(dialog) {
  if (!cartState || cartState.currency !== 'PYG') return false;
  const total = pygAmount(dialog.querySelector('.rsi-total-summary-total-value'));
  const delivery = Array.from(dialog.querySelectorAll('.rsi-total-summary-line-item')).find(row => /envío|shipping/i.test(row.querySelector('.rsi-total-summary-label')?.textContent || ''));
  const shipping = pygAmount(delivery?.querySelector('.rsi-total-summary-value'));
  return total !== null && shipping !== null && total === Number(cartState.total_price) + shipping;
}

function syncForm(dialog) {
  if (directPackPayment()) {
    if (dialog.dataset.essPaymentManaged) {
      delete dialog.dataset.essPaymentManaged;
      delete dialog.dataset.essPaymentReady;
      formStates.delete(dialog);
      dialog.querySelector('.essentia-form-payment')?.remove();
      dialog.querySelector('.essentia-form-payment__error')?.remove();
      const note = dialog.querySelector('[data-ess-payment-note]');
      if (note) {
        note.readOnly = false;
        delete note.dataset.essPaymentNote;
        setNative(note, note.value.split('\n').filter(line => !line.startsWith(MARK)).join('\n'));
      }
    }
    return;
  }
  if (!config || !method() || !dialog.open) return;
  dialog.dataset.essPaymentManaged = method();
  let state = formStates.get(dialog);
  if (!state) {
    state = { appliedOnce: false, ready: false, token: '' };
    formStates.set(dialog, state);
  }
  const token = method() + ':' + wantsDiscount() + ':' + Number(cartState?.total_price || 0);
  if (state.token !== token) { state.token = token; state.appliedOnce = false; state.ready = false; }
  let banner = dialog.querySelector('.essentia-form-payment');
  if (!banner && dialog.querySelector('.rsi-form-header')) {
    banner = document.createElement('div');
    banner.className = 'essentia-form-payment';
    const text = document.createElement('div');
    const small = document.createElement('small');
    small.textContent = copy('selected');
    const strong = document.createElement('strong');
    text.append(small, strong);
    const change = document.createElement('button');
    change.type = 'button';
    change.textContent = copy('change');
    change.addEventListener('click', () => dialog.querySelector('.rsi-form-close-button')?.click());
    banner.append(text, change);
    dialog.querySelector('.rsi-form-header').after(banner);
  }
  if (banner?.querySelector('strong')?.textContent !== label(method())) banner.querySelector('strong').textContent = label(method());
  const note = dialog.querySelector('[name="note"]');
  const requiredNote = [(cartState?.note ?? config.cartNote)?.split('\n').filter(line => !line.startsWith(MARK)).join('\n').trim(), MARK + label(method())].filter(Boolean).join('\n');
  if (note && requiredNote.length <= Number(note.maxLength > 0 ? note.maxLength : 250)) {
    setNative(note, requiredNote);
    note.readOnly = true;
    note.dataset.essPaymentNote = 'true';
  }
  const appliedNode = Array.from(dialog.querySelectorAll('.rsi-discounts-item')).find(item => matchesCode(item.querySelector('.rsi-discounts-remove-button')?.getAttribute('aria-label') || item.textContent));
  if (!wantsDiscount() && appliedNode) {
    appliedNode.querySelector('.rsi-discounts-remove-button')?.click();
    state.ready = false;
    return;
  }
  if (wantsDiscount() && !appliedNode && !state.appliedOnce) {
    const input = dialog.querySelector('.rsi-discounts-input');
    const button = dialog.querySelector('.rsi-discounts-button');
    if (input && button && !button.disabled) {
      state.appliedOnce = true;
      setNative(input, CODE);
      requestAnimationFrame(() => {
        const apply = dialog.querySelector('.rsi-discounts-button');
        if (apply && !apply.disabled) apply.click();
        else state.appliedOnce = false;
      });
    }
  }
  state.ready = !!note && note.value === requiredNote && (!wantsDiscount() || (!!appliedNode && validTransfer(cartState))) && (wantsDiscount() || !appliedNode) && formAmountsMatch(dialog);
  dialog.dataset.essPaymentReady = String(state.ready);
  if (state.ready) formError(dialog, '');
  if (appliedNode) {
    appliedNode.dataset.essTransferCode = 'true';
    const text = appliedNode.querySelector('.rsi-discounts-item-text');
    if (text && text.textContent !== copy('selected_transfer')) text.textContent = copy('selected_transfer');
  }
}

document.addEventListener('click', event => {
  const buy = event.target.closest('button#rsi_buy_now_button');
  if (buy && (buy.closest('#cart-drawer') || buy.closest('.cart-summary__inner'))) {
    if (!directPackPayment() && (busy || !method())) {
      event.preventDefault();
      event.stopImmediatePropagation();
      status(busy ? copy('saving') : copy('choose'), true);
      const control = controls().find(el => el.getClientRects().length) || controls()[0];
      control?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      control?.querySelector('input')?.focus({ preventScroll: true });
    }
  }
  const submit = event.target.closest('.rsi-submit_button');
  if (!submit) return;
  const dialog = submit.closest('#rsi_form_wrapper');
  if (directPackPayment() || !dialog?.dataset.essPaymentManaged) return;
  syncForm(dialog);
  const state = formStates.get(dialog);
  const interior = Array.from(dialog.querySelectorAll('select')).some(select => select.value === '__interior');
  if (busy || !state?.ready || (method() === 'cod' && interior)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    formError(dialog, method() === 'cod' && interior ? copy('interior') : copy('sync_error'));
  }
}, true);

document.addEventListener('submit', event => {
  const dialog = event.target.closest('#rsi_form_wrapper');
  if (directPackPayment() || !dialog?.dataset.essPaymentManaged) return;
  syncForm(dialog);
  if (!formStates.get(dialog)?.ready || busy) {
    event.preventDefault();
    event.stopImmediatePropagation();
    formError(dialog, copy('sync_error'));
  }
}, true);

let queued = false;
let formWasOpen = false;
const schedule = () => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    refreshButtons();
    const openForms = document.querySelectorAll('#rsi_form_wrapper[open]');
    openForms.forEach(syncForm);
    if (openForms.length) formWasOpen = true;
    else if (formWasOpen) {
      formWasOpen = false;
      if (!directPackPayment() && method() && !busy) choose(method());
    }
  });
};
new MutationObserver(records => {
  if (records.some(record => record.type === 'childList' || record.type === 'characterData' || record.attributeName === 'open')) schedule();
}).observe(document.body, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['open'] });
schedule();


document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  event.promise?.then(result => {
    if (result?.detail?.source === 'essentia-payment-choice' || directPackPayment() || !method()) return;
    if (busy) reconcileQueued = true;
    else choose(method());
  }).catch(() => status(copy('sync_error'), true));
});
